import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Timeline. Vertical event list. Mirrors .timeline / .tl in
 * the showcase app.css: a 2px outline rail down the left gutter (pl-6), each
 * item carries an 18px ringed dot (2px colored border + surface fill — NO
 * filled colored chip, NO left bar accent), a bold 14px title, a muted 12px
 * time, and 14px body text.
 */
export const Timeline = React.forwardRef<
  HTMLOListElement,
  React.HTMLAttributes<HTMLOListElement>
>(({ className, children, ...props }, ref) => (
  <ol ref={ref} className={cn('relative pl-6', className)} {...props}>
    <span
      aria-hidden="true"
      className="absolute left-[9px] top-1.5 bottom-1.5 w-0.5 bg-sober-outline"
    />
    {children}
  </ol>
));
Timeline.displayName = 'Timeline';

type TimelineItemVariant = 'primary' | 'green' | 'yellow' | 'gray';

const dotVariantClasses: Record<TimelineItemVariant, string> = {
  primary: 'border-sober-primary text-sober-primary',
  green: 'border-sober-green-fg text-sober-green-fg',
  yellow: 'border-sober-yellow-fg text-sober-yellow-fg',
  gray: 'border-sober-secondary text-sober-secondary',
};

export interface TimelineItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  title: string;
  time?: string;
  variant?: TimelineItemVariant;
}

export const TimelineItem = React.forwardRef<HTMLLIElement, TimelineItemProps>(
  ({ className, title, time, variant = 'primary', children, ...props }, ref) => (
    <li ref={ref} className={cn('relative pb-5 last:pb-0', className)} {...props}>
      <span
        aria-hidden="true"
        className={cn(
          'absolute left-[calc(-1.5rem+2px)] top-0.5 grid h-[18px] w-[18px] place-items-center rounded-full border-2 bg-sober-surface text-[9px]',
          dotVariantClasses[variant]
        )}
      />
      <div className="text-so-14 font-bold text-sober-text">{title}</div>
      {time ? <div className="text-so-12 text-sober-secondary">{time}</div> : null}
      {children ? <div className="mt-0.5 text-so-14 text-sober-text">{children}</div> : null}
    </li>
  )
);
TimelineItem.displayName = 'TimelineItem';
